import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  UserGroupIcon,
  ArrowPathIcon,
  XCircleIcon,
  HandRaisedIcon,
  ArrowTrendingUpIcon,
  CheckBadgeIcon,
  ArrowUpIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, 
  LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

// Registrar os componentes necessários do Chart.js
ChartJS.register(
  ArcElement, 
  BarElement,
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip, 
  Legend
);

const ClientsSection = ({ metrics, isMobile }) => {
  const [period, setPeriod] = useState('month');
  const [clientSegment, setClientSegment] = useState('all');
  
  // Dados simulados para engajamento de clientes
  const clientsData = {
    totalClients: 350,
    newClients: 42,
    returningClients: 145,
    retentionRate: metrics?.clientRetention || 68,
    churnRate: 100 - (metrics?.clientRetention || 68),
    noShowRate: metrics?.noShowRate || 12,
    avgVisitsPerClient: 2.7,
    topClientVisits: 8,
    topClientSpend: 1250,
    feedbackScore: 4.7
  };
  
  // Dados para o gráfico de novos clientes vs retornantes
  const clientTypeData = {
    labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
    datasets: [
      {
        label: 'Novos Clientes',
        data: [35, 40, 38, 45, 42, 48],
        backgroundColor: 'rgba(59, 130, 246, 0.7)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: 'Clientes Recorrentes',
        data: [120, 125, 130, 135, 140, 145],
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 1,
        borderRadius: 4,
      }
    ]
  };
  
  // Dados para o gráfico de taxa de retenção
  const retentionData = {
    labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
    datasets: [
      {
        label: 'Taxa de Retenção',
        data: [62, 64, 65, 67, 65, 68],
        borderColor: 'rgb(139, 92, 246)',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };
  
  // Dados para o gráfico de motivos de ausência
  const noShowData = {
    labels: ['Esquecimento', 'Emergência', 'Insatisfação Prévia', 'Problemas de Agenda', 'Outros'],
    datasets: [
      {
        data: [45, 20, 15, 12, 8],
        backgroundColor: [
          'rgba(99, 102, 241, 0.7)',  // indigo
          'rgba(239, 68, 68, 0.7)',   // vermelho
          'rgba(245, 158, 11, 0.7)',  // laranja
          'rgba(16, 185, 129, 0.7)',  // verde
          'rgba(107, 114, 128, 0.7)'  // cinza
        ],
        borderColor: [
          'rgb(99, 102, 241)',
          'rgb(239, 68, 68)',
          'rgb(245, 158, 11)',
          'rgb(16, 185, 129)',
          'rgb(107, 114, 128)'
        ],
        borderWidth: 1,
      }
    ]
  };
  
  // Dados para o gráfico de segmentação de clientes por frequência
  const frequencyData = {
    labels: ['1x', '2-3x', '4-6x', '+6x'],
    datasets: [
      {
        label: 'Clientes por Frequência',
        data: [140, 110, 70, 30],
        backgroundColor: [
          'rgba(239, 68, 68, 0.7)',   // vermelho (baixo engajamento)
          'rgba(245, 158, 11, 0.7)',  // amarelo (médio engajamento)
          'rgba(16, 185, 129, 0.7)',  // verde (bom engajamento)
          'rgba(79, 70, 229, 0.7)'    // indigo (alto engajamento)
        ],
        borderColor: [
          'rgb(239, 68, 68)',
          'rgb(245, 158, 11)',
          'rgb(16, 185, 129)',
          'rgb(79, 70, 229)'
        ],
        borderWidth: 1,
      }
    ]
  };

  // Opções para gráficos
  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: isMobile ? 10 : 12
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    },
    maintainAspectRatio: false
  };
  
  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y + '%';
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        suggestedMin: 50,
        suggestedMax: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        }
      }
    },
    maintainAspectRatio: false
  };
  
  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          font: {
            size: isMobile ? 10 : 12
          }
        }
      }
    },
    cutout: '65%',
    maintainAspectRatio: false
  };
  
  // Lista simulada de clientes VIP
  const topClients = [
    { id: 1, name: 'Maria Oliveira', visits: 8, spend: 1250, lastVisit: '15/06/2023', favorite: 'Coloração' },
    { id: 2, name: 'Pedro Santos', visits: 7, spend: 980, lastVisit: '02/06/2023', favorite: 'Corte Masculino' },
    { id: 3, name: 'Ana Souza', visits: 6, spend: 1100, lastVisit: '22/05/2023', favorite: 'Hidratação' },
    { id: 4, name: 'João Pereira', visits: 6, spend: 850, lastVisit: '10/06/2023', favorite: 'Barba' },
    { id: 5, name: 'Júlia Costa', visits: 5, spend: 920, lastVisit: '05/06/2023', favorite: 'Manicure' }
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Engajamento dos Clientes</h2>
      
      {/* Cards de estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Total de Clientes</h3>
            <div className="bg-blue-100 p-2 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">{clientsData.totalClients}</p>
          <div className="mt-2 flex items-center text-xs">
            <ArrowTrendingUpIcon className="h-3 w-3 text-green-500 mr-1" />
            <span className="text-green-500 font-medium">+15%</span>
            <span className="text-gray-400 ml-1">vs. período anterior</span>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Taxa de Retenção</h3>
            <div className="bg-purple-100 p-2 rounded-lg">
              <ArrowPathIcon className="h-5 w-5 text-purple-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">{clientsData.retentionRate}%</p>
          <div className="mt-2 flex items-center text-xs">
            <ArrowTrendingUpIcon className="h-3 w-3 text-green-500 mr-1" />
            <span className="text-green-500 font-medium">+3%</span>
            <span className="text-gray-400 ml-1">vs. período anterior</span>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Taxa de Faltas</h3>
            <div className="bg-red-100 p-2 rounded-lg">
              <XCircleIcon className="h-5 w-5 text-red-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">{clientsData.noShowRate}%</p>
          <div className="mt-2 flex items-center text-xs">
            <ArrowTrendingUpIcon className="h-3 w-3 text-red-500 mr-1 rotate-180" />
            <span className="text-green-500 font-medium">-2%</span>
            <span className="text-gray-400 ml-1">vs. período anterior</span>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Visitas/Cliente</h3>
            <div className="bg-green-100 p-2 rounded-lg">
              <HandRaisedIcon className="h-5 w-5 text-green-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">{clientsData.avgVisitsPerClient}</p>
          <div className="mt-2 flex items-center text-xs">
            <ArrowTrendingUpIcon className="h-3 w-3 text-green-500 mr-1" />
            <span className="text-green-500 font-medium">+0.3</span>
            <span className="text-gray-400 ml-1">vs. período anterior</span>
          </div>
        </motion.div>
      </div>
      
      {/* Gráficos de análise */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Novos vs. Recorrentes</h3>
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
              <button 
                onClick={() => setPeriod('month')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                  period === 'month' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Mês
              </button>
              <button 
                onClick={() => setPeriod('quarter')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                  period === 'quarter' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Trimestre
              </button>
              <button 
                onClick={() => setPeriod('year')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                  period === 'year' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Ano
              </button>
            </div>
          </div>
          
          <div className="h-64">
            <Bar data={clientTypeData} options={barOptions} />
          </div>
          
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="flex justify-between mb-1">
                <p className="text-xs text-blue-700">Novos Clientes</p>
                <span className="text-xs font-medium text-blue-700">+14%</span>
              </div>
              <p className="text-sm font-bold text-blue-800">{clientsData.newClients} este mês</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <div className="flex justify-between mb-1">
                <p className="text-xs text-green-700">Recorrentes</p>
                <span className="text-xs font-medium text-green-700">+8%</span>
              </div>
              <p className="text-sm font-bold text-green-800">{clientsData.returningClients} este mês</p>
            </div>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <h3 className="text-lg font-semibold mb-4">Taxa de Retenção</h3>
          <div className="h-64">
            <Line data={retentionData} options={lineOptions} />
          </div>
          
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-xs text-purple-700 mb-1">Retorno médio</p>
              <div className="flex items-center">
                <CalendarDaysIcon className="h-4 w-4 text-purple-700 mr-1" />
                <p className="text-sm font-bold text-purple-800">A cada 45 dias</p>
              </div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <p className="text-xs text-red-700 mb-1">Taxa de abandono</p>
              <div className="flex items-center">
                <ArrowUpIcon className="h-4 w-4 text-red-700 mr-1 rotate-45" />
                <p className="text-sm font-bold text-red-800">{clientsData.churnRate}% ao mês</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Motivos de faltas e segmentação de clientes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <h3 className="text-lg font-semibold mb-4">Motivos de Faltas</h3>
          <div className="h-64">
            <Doughnut data={noShowData} options={doughnutOptions} />
          </div>
          
          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-blue-800">Recomendações</p>
            <p className="text-xs text-blue-700 mt-1">
              Implementar sistema de confirmação 24h antes e enviar lembretes por WhatsApp 
              2h antes para reduzir faltas por esquecimento em até 70%.
            </p>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Segmentação de Clientes</h3>
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
              <button 
                onClick={() => setClientSegment('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                  clientSegment === 'all' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Todos
              </button>
              <button 
                onClick={() => setClientSegment('frequency')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                  clientSegment === 'frequency' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Frequência
              </button>
              <button 
                onClick={() => setClientSegment('spend')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                  clientSegment === 'spend' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Gasto
              </button>
            </div>
          </div>
          
          <div className="h-64">
            <Doughnut data={frequencyData} options={{...doughnutOptions, plugins: {...doughnutOptions.plugins, legend: {...doughnutOptions.plugins.legend, position: 'bottom'}}}} />
          </div>
          
          <div className="mt-3 p-3 bg-indigo-50 rounded-lg">
            <p className="text-sm font-medium text-indigo-800">Oportunidade</p>
            <p className="text-xs text-indigo-700 mt-1">
              40% dos clientes visitam apenas 1x. Crie uma campanha de "Segunda visita com 15% 
              de desconto" para converter clientes únicos em recorrentes.
            </p>
          </div>
        </motion.div>
      </div>
      
      {/* Tabela de clientes VIP */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Top 5 Clientes</h3>
          <span className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center">
            <CheckBadgeIcon className="h-3 w-3 mr-1" />
            VIP
          </span>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visitas</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gasto Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Última Visita</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serviço Favorito</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {topClients.map((client, index) => (
                <tr key={client.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{client.name}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{client.visits}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">R$ {client.spend}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{client.lastVisit}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {client.favorite}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="mt-4 flex justify-between">
          <button className="text-sm text-indigo-600 font-medium hover:text-indigo-700">
            Ver todos os clientes
          </button>
          <button className="text-sm text-indigo-600 font-medium hover:text-indigo-700 flex items-center">
            <span>Exportar lista VIP</span>
            <ArrowTrendingUpIcon className="h-4 w-4 ml-1 rotate-90" />
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ClientsSection; 